import React, { Fragment, useState, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useHistory } from 'react-router-dom';

import { Motion, spring } from 'react-motion';

import './stylesheet.css';

import { List, Map } from 'immutable';

import * as orgActions from '../../../../actions/org';
import * as captureActions from '../../../../actions/capture';
import * as baseActions from '../../../../actions/base';

import sampleCaptureTemplates from '../../../../lib/sample_capture_templates';

import ActionButton from './components/ActionButton/';
import { determineIncludedFiles } from '../../../../reducers/org';
import { STATIC_FILE_PREFIX } from '../../../../lib/org_utils';
import {
  dashboardConfigFromState,
  formatDatePath,
  filePathToUrl,
  directoryOfPath,
  neighbourPath,
} from '../../../../lib/dashboard';
import { snippetsAreAvailable } from '../../../../lib/snippets';
import DaystartPrompt from '../../../DaystartPrompt';
import SnippetPicker from '../../../SnippetPicker';

const ActionDrawer = ({
  org,
  selectedHeaderId,
  base,
  staticFile,
  captureTemplates,
  path,
  selectedTableCellId,
  selectedListItemId,
  isLoading,
  online,
  shouldDisableSyncButtons,
  activeClocks,
  syncBackendClient,
  snippetsRepositoryUrl,
  storedDashboardConfig,
}) => {
  const [isDisplayingArrowButtons, setIsDisplayingArrowButtons] = useState(false);
  const [isDisplayingCaptureButtons, setIsDisplayingCaptureButtons] = useState(false);
  const [isDisplayingNavigationButtons, setIsDisplayingNavigationButtons] = useState(false);
  // The daily note we are on our way to, once it turns out it may not exist yet.
  const [daystartTargetPath, setDaystartTargetPath] = useState(null);
  const [isPickingSnippet, setIsPickingSnippet] = useState(false);
  const history = useHistory();

  const mainArrowButton = useRef(null);

  const mainArrowButtonBoundingRect = useMemo(
    () => (!!mainArrowButton.current ? mainArrowButton.current.getBoundingClientRect() : null),
    [mainArrowButton]
  );

  const handleUpClick = () =>
    !!selectedHeaderId
      ? org.moveHeaderUp(selectedHeaderId)
      : !!selectedTableCellId
      ? org.moveTableRowUp()
      : org.moveListItemUp();

  const handleDownClick = () =>
    !!selectedHeaderId
      ? org.moveHeaderDown(selectedHeaderId)
      : !!selectedTableCellId
      ? org.moveTableRowDown()
      : org.moveListItemDown();

  const handleLeftClick = () =>
    !!selectedHeaderId
      ? org.moveHeaderLeft(selectedHeaderId)
      : !!selectedTableCellId
      ? org.moveTableColumnLeft()
      : org.moveListItemLeft();

  const handleRightClick = () =>
    !!selectedHeaderId
      ? org.moveHeaderRight(selectedHeaderId)
      : !!selectedTableCellId
      ? org.moveTableColumnRight()
      : org.moveListItemRight();

  const handleMoveSubtreeLeftClick = () =>
    !!selectedHeaderId ? org.moveSubtreeLeft(selectedHeaderId) : org.moveListSubtreeLeft();

  const handleMoveSubtreeRightClick = () =>
    !!selectedHeaderId ? org.moveSubtreeRight(selectedHeaderId) : org.moveListSubtreeRight();

  const handleCaptureButtonClick = (template) => () => {
    setIsDisplayingCaptureButtons(false);
    base.activatePopup('capture', {
      templateId: template.get('id'),
      templateDescription: template.get('description'),
    });
  };

  const getSampleCaptureTemplates = () => sampleCaptureTemplates;

  const getAvailableCaptureTemplates = () =>
    staticFile === 'sample'
      ? getSampleCaptureTemplates()
      : captureTemplates.filter(
          (template) =>
            template.get('isAvailableInAllOrgFiles') ||
            template
              .get('orgFilesWhereAvailable')
              .map((availablePath) =>
                availablePath.trim().startsWith('/')
                  ? availablePath.trim()
                  : '/' + availablePath.trim()
              )
              .includes((path || '').trim())
        );

  const handleSync = () => org.sync({ forceAction: 'manual' });

  const handleMainArrowButtonClick = () => setIsDisplayingArrowButtons(!isDisplayingArrowButtons);

  const handleSearchButtonClick = () => {
    base.activatePopup('search');
  };

  // Snippets are offered from the capture menu, so it is worth opening even with no
  // capture templates configured — hence checking both before complaining.
  const canInsertSnippets = snippetsAreAvailable(syncBackendClient, snippetsRepositoryUrl);

  const handleMainCaptureButtonClick = () => {
    if (
      !isDisplayingCaptureButtons &&
      getAvailableCaptureTemplates().size === 0 &&
      !canInsertSnippets
    ) {
      alert(
        `You don't have any capture templates set up for this file! Add some in Settings > Capture Templates`
      );
      return;
    }

    setIsDisplayingCaptureButtons(!isDisplayingCaptureButtons);
  };

  const handleInsertSnippetClick = () => {
    setIsDisplayingCaptureButtons(false);
    setIsPickingSnippet(true);
  };

  // ── Datei-Navigation (zweistufig wie die Bewegungsknöpfe) ───────────────────────
  // „heute“ kommt aus derselben Konfiguration wie das Dashboard, damit es überall
  // dasselbe bedeutet. Der erste Knopf führt aufs Dashboard selbst — von dort sind
  // GTD und alle konfigurierten Ziele einen Griff entfernt.
  // Read from the store, not localStorage: the configuration is a persisted setting
  // now, so it may have arrived from the sync backend on this device.
  const dashboardConfig = useMemo(() => dashboardConfigFromState(storedDashboardConfig), [
    storedDashboardConfig,
  ]);

  const goToFile = (targetPath) => {
    if (!targetPath) return;
    setIsDisplayingNavigationButtons(false);
    if (targetPath === path) {
      base.setDisappearingLoadingMessage('Already here.', 1500);
      return;
    }
    history.push(filePathToUrl(targetPath));
  };

  // Wie in der <ActionDrawer> des FileBrowsers: das Dashboard ist eine eigene
  // Route, keine Datei, also nicht über goToFile().
  const handleDashboardClick = () => {
    setIsDisplayingNavigationButtons(false);
    history.push('/dashboard');
  };

  // What links *to* this file, and what it links *out* to, both read from the
  // org-roam index. Each closes the button stack first, like the other
  // navigation actions, so the popup is not opened behind a fan of buttons.
  const handleBacklinksClick = () => {
    setIsDisplayingNavigationButtons(false);
    base.activatePopup('backlinks');
  };

  const handleForwardLinksClick = () => {
    setIsDisplayingNavigationButtons(false);
    base.activatePopup('forward-links');
  };

  // Not goToFile(): today's note may not exist yet, and <DaystartPrompt> is what
  // knows how to offer creating it from snippets. It navigates by itself once the
  // file is there — or straight away, if it already was.
  const handleTodayClick = () => {
    setIsDisplayingNavigationButtons(false);
    setDaystartTargetPath(formatDatePath(dashboardConfig.dailyPathTemplate));
  };

  // „Davor“ und „danach“ heißt: die nächste TATSÄCHLICH vorhandene Datei im gleichen
  // Verzeichnis — nicht das Datum minus eins. Tagesnotizen entstehen nicht täglich, und
  // ein Sprung auf eine leere Datei wäre nur ein Umweg über eine Fehlermeldung.
  const handleNeighbourClick = (direction) => async () => {
    if (!path) return;
    if (!syncBackendClient) {
      base.setDisappearingLoadingMessage('Not connected to a sync backend.', 2000);
      return;
    }
    const directory = directoryOfPath(path);
    base.setLoadingMessage('Looking for neighbouring files...');
    try {
      const { listing } = await syncBackendClient.getDirectoryListing(directory);
      const paths = (listing || [])
        .filter((entry) => !entry.get('isDirectory') && /\.org$/i.test(entry.get('name') || ''))
        .map((entry) => entry.get('path'))
        .toJS()
        .sort();
      base.hideLoadingMessage();
      if (!paths.length) {
        base.setDisappearingLoadingMessage('No Org files in this folder.', 2000);
        return;
      }
      const target = neighbourPath(paths, path, direction);
      if (!target) {
        base.setDisappearingLoadingMessage(
          direction < 0
            ? 'This is the first file in the folder.'
            : 'This is the last file in the folder.',
          2000
        );
        return;
      }
      goToFile(target);
    } catch (error) {
      base.hideLoadingMessage();
      base.setDisappearingLoadingMessage(`Could not list ${directory}.`, 2500);
      console.error(error);
    }
  };

  const handleMainNavigationButtonClick = () =>
    setIsDisplayingNavigationButtons(!isDisplayingNavigationButtons);

  const renderNavigationButtons = () => {
    const baseNavigationButtonStyle = {
      position: 'absolute',
      zIndex: 0,
      left: 0,
      opacity: isDisplayingArrowButtons || isDisplayingCaptureButtons ? 0 : 1,
    };
    if (!isDisplayingNavigationButtons) {
      baseNavigationButtonStyle.boxShadow = 'none';
    }

    const mainButtonStyle = {
      opacity: isDisplayingArrowButtons || isDisplayingCaptureButtons ? 0 : 1,
      pointerEvents: isDisplayingArrowButtons || isDisplayingCaptureButtons ? 'none' : 'all',
      position: 'relative',
      zIndex: 1,
    };

    const animatedStyle = {
      bottom: spring(isDisplayingNavigationButtons ? 70 : 0, { stiffness: 300 }),
    };

    const ziele = [
      { iconName: 'tachometer-alt', onClick: handleDashboardClick, tooltip: 'Open dashboard' },
      {
        iconName: 'calendar-check', // FontAwesome 5.1 kennt kein calendar-day
        onClick: handleTodayClick,
        tooltip: `Go to today: ${formatDatePath(dashboardConfig.dailyPathTemplate)}`,
      },
      {
        iconName: 'backward',
        onClick: handleNeighbourClick(-1),
        tooltip: 'Previous file in this folder',
      },
      {
        iconName: 'forward',
        onClick: handleNeighbourClick(1),
        tooltip: 'Next file in this folder',
      },
      // Last in the list means highest in the stack: the buttons are laid out
      // upward from the main one at `bottom * (index + 1)`. The two link
      // directions stay adjacent, inbound below outbound.
      {
        iconName: 'link',
        onClick: handleBacklinksClick,
        tooltip: 'Back-links to this file',
      },
      {
        iconName: 'external-link-alt',
        onClick: handleForwardLinksClick,
        tooltip: 'Forward-links from this file',
      },
    ];

    return (
      <Motion style={animatedStyle}>
        {(style) => (
          <div className="action-drawer__capture-buttons-container">
            <ActionButton
              iconName={isDisplayingNavigationButtons ? 'times' : 'compass'}
              isDisabled={false}
              onClick={handleMainNavigationButtonClick}
              style={mainButtonStyle}
              dataTestId="navigation-main-button"
              tooltip={
                isDisplayingNavigationButtons
                  ? 'Hide navigation buttons'
                  : 'Show navigation buttons'
              }
            />

            {ziele.map((ziel, index) => (
              <ActionButton
                key={ziel.iconName}
                iconName={ziel.iconName}
                isDisabled={false}
                onClick={ziel.onClick}
                style={{ ...baseNavigationButtonStyle, bottom: style.bottom * (index + 1) }}
                dataTestId={`navigation-${ziel.iconName}`}
                tooltip={ziel.tooltip}
              />
            ))}
          </div>
        )}
      </Motion>
    );
  };

  const renderCaptureButtons = () => {
    const availableCaptureTemplates = getAvailableCaptureTemplates();

    const baseCaptureButtonStyle = {
      position: 'absolute',
      zIndex: 0,
      left: 0,
      opacity: isDisplayingArrowButtons ? 0 : 1,
    };
    if (!isDisplayingCaptureButtons) {
      baseCaptureButtonStyle.boxShadow = 'none';
    }

    const mainButtonStyle = {
      opacity: isDisplayingArrowButtons ? 0 : 1,
      position: 'relative',
      zIndex: 1,
    };

    const animatedStyle = {
      bottom: spring(isDisplayingCaptureButtons ? 70 : 0, { stiffness: 300 }),
    };

    return (
      <Motion style={animatedStyle}>
        {(style) => (
          <div className="action-drawer__capture-buttons-container">
            <ActionButton
              iconName={isDisplayingCaptureButtons ? 'times' : 'plus'}
              isDisabled={false}
              onClick={handleMainCaptureButtonClick}
              style={mainButtonStyle}
              dataTestId="capture-main-button"
              tooltip={
                isDisplayingCaptureButtons ? 'Hide capture templates' : 'Show capture templates'
              }
            />

            {availableCaptureTemplates.map((template, index) => (
              <ActionButton
                key={template.get('id')}
                letter={template.get('letter')}
                iconName={template.get('iconName')}
                isDisabled={false}
                onClick={handleCaptureButtonClick(template)}
                style={{ ...baseCaptureButtonStyle, bottom: style.bottom * (index + 1) }}
                dataTestId={`capture-template-${template
                  .get('description')
                  .toLowerCase()
                  .replace(/\s+/g, '-')}`}
                tooltip={`Activate "${template.get('description')}" capture template`}
              />
            ))}

            {canInsertSnippets && (
              <ActionButton
                iconName="puzzle-piece"
                isDisabled={false}
                onClick={handleInsertSnippetClick}
                style={{
                  ...baseCaptureButtonStyle,
                  bottom: style.bottom * (availableCaptureTemplates.size + 1),
                }}
                dataTestId="capture-insert-snippet"
                tooltip="Insert a snippet at the end of this file"
              />
            )}
          </div>
        )}
      </Motion>
    );
  };

  const renderMovementButtons = () => {
    const baseArrowButtonStyle = {
      opacity: isDisplayingCaptureButtons ? 0 : 1,
    };
    if (!isDisplayingArrowButtons) {
      baseArrowButtonStyle.boxShadow = 'none';
    }

    let centerXOffset = 0;
    if (!!mainArrowButtonBoundingRect) {
      centerXOffset =
        window.screen.width / 2 -
        (mainArrowButtonBoundingRect.x + mainArrowButtonBoundingRect.width / 2);
    }

    const animatedStyles = {
      centerXOffset: spring(isDisplayingArrowButtons ? centerXOffset : 0, { stiffness: 300 }),
      topRowYOffset: spring(isDisplayingArrowButtons ? 150 : 0, { stiffness: 300 }),
      bottomRowYOffset: spring(isDisplayingArrowButtons ? 80 : 0, { stiffness: 300 }),
      firstColumnXOffset: spring(isDisplayingArrowButtons ? 70 : 0, {
        stiffness: 300,
      }),
      secondColumnXOffset: spring(isDisplayingArrowButtons ? 140 : 0, {
        stiffness: 300,
      }),
    };

    let subIconNameStr = null;
    let tooltipUpStr = 'Move header up';
    let tooltipDownStr = 'Move header down';
    let tooltipLeftStr = 'Move header left';
    let tooltipRightStr = 'Move header right';
    if (!!selectedTableCellId) {
      subIconNameStr = 'table';
      tooltipUpStr = 'Move row up';
      tooltipDownStr = 'Move row down';
      tooltipLeftStr = 'Move column left';
      tooltipRightStr = 'Move column right';
    } else if (!!selectedListItemId) {
      subIconNameStr = 'list';
      tooltipUpStr = 'Move list up';
      tooltipDownStr = 'Move list down';
      tooltipLeftStr = 'Move list left';
      tooltipRightStr = 'Move list right';
    }

    return (
      <Motion style={animatedStyles}>
        {(style) => (
          <div
            className="action-drawer__arrow-buttons-container"
            style={{
              left: style.centerXOffset,
              pointerEvents: isDisplayingCaptureButtons ? 'none' : 'all',
            }}
          >
            <ActionButton
              additionalClassName="action-drawer__arrow-button"
              iconName="arrow-up"
              subIconName={subIconNameStr}
              isDisabled={false}
              onClick={handleUpClick}
              style={{ ...baseArrowButtonStyle, bottom: style.topRowYOffset }}
              tooltip={tooltipUpStr}
            />
            <ActionButton
              additionalClassName="action-drawer__arrow-button"
              iconName="arrow-down"
              subIconName={subIconNameStr}
              isDisabled={false}
              onClick={handleDownClick}
              style={{ ...baseArrowButtonStyle, bottom: style.bottomRowYOffset }}
              tooltip={tooltipDownStr}
            />
            <ActionButton
              additionalClassName="action-drawer__arrow-button"
              iconName="arrow-left"
              subIconName={subIconNameStr}
              isDisabled={false}
              onClick={handleLeftClick}
              style={{
                ...baseArrowButtonStyle,
                bottom: style.bottomRowYOffset,
                right: style.firstColumnXOffset,
              }}
              tooltip={tooltipLeftStr}
            />
            <ActionButton
              additionalClassName="action-drawer__arrow-button"
              iconName="arrow-right"
              subIconName={subIconNameStr}
              isDisabled={false}
              onClick={handleRightClick}
              style={{
                ...baseArrowButtonStyle,
                bottom: style.bottomRowYOffset,
                left: style.firstColumnXOffset,
              }}
              tooltip={tooltipRightStr}
            />
            {!selectedTableCellId && (
              <Fragment>
                <ActionButton
                  additionalClassName="action-drawer__arrow-button"
                  iconName="chevron-left"
                  subIconName={subIconNameStr}
                  isDisabled={false}
                  onClick={handleMoveSubtreeLeftClick}
                  style={{
                    ...baseArrowButtonStyle,
                    bottom: style.bottomRowYOffset,
                    right: style.secondColumnXOffset,
                  }}
                  tooltip="Move entire subtree left"
                />
                <ActionButton
                  additionalClassName="action-drawer__arrow-button"
                  iconName="chevron-right"
                  subIconName={subIconNameStr}
                  isDisabled={false}
                  onClick={handleMoveSubtreeRightClick}
                  style={{
                    ...baseArrowButtonStyle,
                    bottom: style.bottomRowYOffset,
                    left: style.secondColumnXOffset,
                  }}
                  tooltip="Move entire subtree right"
                />
              </Fragment>
            )}

            <ActionButton
              iconName={isDisplayingArrowButtons ? 'times' : 'arrows-alt'}
              subIconName={subIconNameStr}
              additionalClassName="action-drawer__main-arrow-button"
              isDisabled={false}
              onClick={handleMainArrowButtonClick}
              style={{
                opacity: isDisplayingCaptureButtons ? 0 : 1,
                pointerEvents: isDisplayingCaptureButtons ? 'none' : 'all',
              }}
              tooltip={isDisplayingArrowButtons ? 'Hide movement buttons' : 'Show movement buttons'}
              onRef={mainArrowButton}
            />
          </div>
        )}
      </Motion>
    );
  };

  const handleAgendaClick = () => base.activatePopup('agenda');

  return (
    <div className="action-drawer-container nice-scroll">
      {!!daystartTargetPath && (
        <DaystartPrompt
          targetPath={daystartTargetPath}
          onClose={() => setDaystartTargetPath(null)}
        />
      )}

      {isPickingSnippet && <SnippetPicker onClose={() => setIsPickingSnippet(false)} />}

      {
        <Fragment>
          <ActionButton
            iconName="cloud"
            subIconName="sync-alt"
            shouldSpinSubIcon={isLoading}
            isDisabled={shouldDisableSyncButtons || !online}
            onClick={handleSync}
            style={{
              opacity: isDisplayingArrowButtons || isDisplayingCaptureButtons ? 0 : 1,
              pointerEvents:
                isDisplayingArrowButtons || isDisplayingCaptureButtons ? 'none' : 'all',
            }}
            tooltip="Sync changes"
          />

          <ActionButton
            iconName="calendar-alt"
            isDisabled={false}
            onClick={handleAgendaClick}
            style={{
              opacity: isDisplayingArrowButtons || isDisplayingCaptureButtons ? 0 : 1,
              pointerEvents:
                isDisplayingArrowButtons || isDisplayingCaptureButtons ? 'none' : 'all',
            }}
            tooltip="Show agenda"
          />

          {renderMovementButtons()}

          <ActionButton
            iconName={'search'}
            isDisabled={false}
            onClick={handleSearchButtonClick}
            additionalClassName={activeClocks !== 0 ? 'active-clock-indicator' : undefined}
            style={{
              opacity: isDisplayingArrowButtons || isDisplayingCaptureButtons ? 0 : 1,
              position: 'relative',
              zIndex: 1,
              pointerEvents:
                isDisplayingArrowButtons || isDisplayingCaptureButtons ? 'none' : 'all',
            }}
            tooltip="Show Search / Task List"
          />

          {!!path &&
            !staticFile &&
            !`${path}`.startsWith(STATIC_FILE_PREFIX) &&
            renderNavigationButtons()}

          {renderCaptureButtons()}
        </Fragment>
      }
    </div>
  );
};

const mapStateToProps = (state) => {
  const path = state.org.present.get('path');
  const files = state.org.present.get('files');
  const file = state.org.present.getIn(['files', path], Map());
  const fileSettings = state.org.present.get('fileSettings');
  const searchFiles = determineIncludedFiles(files, fileSettings, path, 'includeInSearch', false);
  const activeClocks = Object.values(
    searchFiles.map((f) => (f.get('headers').size ? f.get('activeClocks') : 0)).toJS()
  ).reduce((acc, val) => (typeof val === 'number' ? acc + val : acc), 0);
  return {
    selectedHeaderId: file.get('selectedHeaderId'),
    isDirty: file.get('isDirty'),
    isNarrowedHeaderActive: !!file.get('narrowedHeaderId'),
    selectedTableCellId: file.get('selectedTableCellId'),
    selectedListItemId: file.get('selectedListItemId'),
    captureTemplates: state.capture.get('captureTemplates', List()),
    path,
    isLoading: !state.base.get('isLoading').isEmpty(),
    online: state.base.get('online'),
    snippetsRepositoryUrl: state.base.get('snippetsRepositoryUrl') || '',
    storedDashboardConfig: state.base.get('dashboardConfig'),
    activeClocks,
    syncBackendClient: state.syncBackend.get('client'),
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    org: bindActionCreators(orgActions, dispatch),
    capture: bindActionCreators(captureActions, dispatch),
    base: bindActionCreators(baseActions, dispatch),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ActionDrawer);
